import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, KeyRound, AlertCircle, CheckCircle2 } from 'lucide-react';
import PasswordInput from '../components/common/PasswordInput';
import AuthLayout from '../components/common/AuthLayout';

const ResetPasswordPage = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { updatePassword, recovery } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // If we're not in recovery mode, redirect to login
        // This happens if someone tries to access /reset-password directly
        if (!recovery && !isLoading && !isSuccess) {
            const timer = setTimeout(() => {
                if (!recovery) navigate('/login');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [recovery, navigate, isLoading, isSuccess]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            await updatePassword(password);
            setIsSuccess(true);
            // Wait a bit then redirect
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.message || 'Failed to update password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <AuthLayout>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-full text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={48} />
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Password updated</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Your password has been successfully reset. Redirecting you to login...</p>
                </div>
            </AuthLayout>
        );
    }

    if (!recovery && !isLoading) {
        return (
            <AuthLayout>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-full text-amber-600 dark:text-amber-400">
                        <AlertCircle size={48} />
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Invalid link</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">This password reset link is invalid or has expired. Redirecting you...</p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div className="space-y-2">
                <div className="mb-4 inline-flex p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl text-emerald-600 dark:text-emerald-400">
                    <KeyRound size={28} />
                </div>
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Set new password</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Please enter your new password below</p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                    <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl">
                        {error}
                    </div>
                )}

                <div className="space-y-4">
                    <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="New password"
                        autoComplete="new-password"
                        required
                    />

                    <PasswordInput
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        required
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30"
                >
                    {isLoading ? (
                        <Loader2 className="animate-spin" size={20} />
                    ) : (
                        <span>Update Password</span>
                    )}
                </button>
            </form>
        </AuthLayout>
    );
};

export default ResetPasswordPage;
